import React, { useEffect, useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { API_BASE_URL } from "src/config/apiBase";
import { decryptToken } from "../utils/getToken";

const STAGE_COLORS = {
  Converted: "#10b981",
  "In Progress": "#f59e0b",
  Pending: "#3b82f6",
  "Not Interested": "#ef4444",
  Uncontacted: "#6366f1",
  Cancelled: "#94a3b8",
};

const pickColor = (name, index) =>
  STAGE_COLORS[name] || ["#6366f1", "#3b82f6", "#f59e0b", "#10b981", "#ef4444", "#8b5cf6"][index % 6];

const CrmAnalyticsCharts = ({ days = 30 }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const url = API_BASE_URL;
  const token = decryptToken("accessToken") || "";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${url}/leads/crm-analytics?days=${days}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled) setStats(data);
      })
      .catch(() => {
        if (!cancelled) setStats(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [days, url, token]);

  if (loading) {
    return <p className="crm-insights__muted">Loading charts…</p>;
  }

  if (!stats?.funnel?.length && !stats?.sources?.length) {
    return <p className="crm-insights__muted">Not enough CRM data to chart yet.</p>;
  }

  const funnelData = (stats.funnel || []).map((row, i) => ({
    name: row.stage,
    value: row.count,
    fill: pickColor(row.stage, i),
  }));

  const sourceData = (stats.sources || []).slice(0, 8).map((row) => ({
    name: row.name.length > 18 ? `${row.name.slice(0, 16)}…` : row.name,
    fullName: row.name,
    leads: row.count,
    converted: row.converted,
    conversionRate: row.conversionRate,
  }));

  return (
    <div className="crm-analytics-charts">
      {funnelData.length > 0 && (
        <div className="crm-analytics-charts__card">
          <h3 className="crm-analytics-charts__title">Pipeline breakdown</h3>
          <ResponsiveContainer width="100%" height={320}>
            <PieChart>
              <Pie
                data={funnelData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={70}
                outerRadius={110}
                paddingAngle={2}
              >
                {funnelData.map((entry) => (
                  <Cell key={entry.name} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip formatter={(value, name) => [value, name]} />
            </PieChart>
          </ResponsiveContainer>
          <div className="crm-analytics-charts__legend">
            {funnelData.map((row) => (
              <span key={row.name} className="crm-analytics-charts__legend-item">
                <i style={{ background: row.fill }} />
                {row.name} ({row.value})
              </span>
            ))}
          </div>
        </div>
      )}

      {sourceData.length > 0 && (
        <div className="crm-analytics-charts__card">
          <h3 className="crm-analytics-charts__title">Leads by source</h3>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={sourceData} margin={{ top: 8, right: 8, left: 0, bottom: 48 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" angle={-30} textAnchor="end" interval={0} height={60} tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(value, key) => [value, key === "leads" ? "Leads" : key]}
                labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName || ""}
              />
              <Bar dataKey="leads" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Leads" />
              <Bar dataKey="converted" fill="#10b981" radius={[4, 4, 0, 0]} name="Converted" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default CrmAnalyticsCharts;
